import { Avatar, Input, Typography, Divider, Skeleton, Empty } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import useMyQuery from "@/hooks/useMyQuery";
import { useState, useCallback, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import { formatThousands } from "@/helpers";
import { useCurrencyContext } from "@/context/CurrencyContext";
import { useLanguageContext } from "@/context/LanguageContext";

interface TokenFlow {
  symbol: string;
  logo_uri: string;
  name: string;
  no_deposit: number;
  no_txns: number;
  no_user_deposit: number;
  no_user_withdraw: number;
  no_withdraw: number;
  users: number;
  vol_deposit: number;
  vol_withdraw: number;
  volume: number;
}

interface TokenFlowResponse {
  success: boolean;
  data: {
    total: number;
    result: TokenFlow[];
  };
}

const TokenFlowSkeleton = () => (
  <div>
    <div className="p-4">
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton.Avatar active size={32} />
          <div className="flex items-center gap-2">
            <Skeleton.Button active size="small" style={{ width: 60 }} />
            <Skeleton.Button active size="small" style={{ width: 100 }} />
          </div>
        </div>

        <div className="space-y-6">
          {/* Volume section */}
          <div className="flex justify-between items-center gap-4">
            <div className="w-[120px]">
              <Skeleton.Input active size="small" block style={{ width: 60 }} />
              <Skeleton.Input active size="small" block style={{ width: 100 }} className="mt-2" />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex justify-between">
                <Skeleton.Input active size="small" style={{ width: 80 }} />
                <Skeleton.Input active size="small" style={{ width: 80 }} />
              </div>
              <div className="flex justify-between">
                <Skeleton.Input active size="small" style={{ width: 60 }} />
                <Skeleton.Input active size="small" style={{ width: 60 }} />
              </div>
              <div className="flex h-2 bg-gray-200 rounded-full" />
            </div>
          </div>

          {/* Txs section */}
          <div className="flex justify-between items-center gap-4">
            <div className="w-[120px]">
              <Skeleton.Input active size="small" block style={{ width: 60 }} />
              <Skeleton.Input active size="small" block style={{ width: 100 }} className="mt-2" />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex justify-between">
                <Skeleton.Input active size="small" style={{ width: 80 }} />
                <Skeleton.Input active size="small" style={{ width: 80 }} />
              </div>
              <div className="flex justify-between">
                <Skeleton.Input active size="small" style={{ width: 60 }} />
                <Skeleton.Input active size="small" style={{ width: 60 }} />
              </div>
              <div className="flex h-2 bg-gray-200 rounded-full" />
            </div>
          </div>

          {/* Users section */}
          <div className="flex justify-between items-center gap-4">
            <div className="w-[120px]">
              <Skeleton.Input active size="small" block style={{ width: 60 }} />
              <Skeleton.Input active size="small" block style={{ width: 100 }} className="mt-2" />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex justify-between">
                <Skeleton.Input active size="small" style={{ width: 80 }} />
                <Skeleton.Input active size="small" style={{ width: 80 }} />
              </div>
              <div className="flex justify-between">
                <Skeleton.Input active size="small" style={{ width: 60 }} />
                <Skeleton.Input active size="small" style={{ width: 60 }} />
              </div>
              <div className="flex h-2 bg-gray-200 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <Divider className="!my-2" />
  </div>
);

const calculatePercentage = (value: number, total: number) => {
  if (!total) return 0;
  return Number(((value / total) * 100).toFixed(0));
};

interface MetricSectionProps {
  title: string;
  value: string | number;
  leftLabel: string;
  rightLabel: string;
  leftValue: string;
  rightValue: string;
  leftPercent: number;
  rightPercent: number;
}

const MetricSection = ({
  title,
  value,
  leftLabel,
  rightLabel,
  leftValue,
  rightValue,
  leftPercent,
  rightPercent,
}: MetricSectionProps) => (
  <div className="flex justify-between items-center gap-4">
    <div className="w-[120px]">
      <Typography.Title level={5} type="secondary" className="m-0">
        {title}
      </Typography.Title>
      <Typography.Title level={5} className="m-0">
        {value}
      </Typography.Title>
    </div>
    <div className="flex-1 flex flex-col gap-1">
      <div className="flex justify-between">
        <Typography.Text type="secondary">{leftLabel}</Typography.Text>
        <Typography.Text type="secondary">{rightLabel}</Typography.Text>
      </div>
      <div className="flex justify-between">
        <Typography.Text strong type="secondary">
          {leftValue}
        </Typography.Text>
        <Typography.Text strong type="secondary">
          {rightValue}
        </Typography.Text>
      </div>
      <div className="flex h-2">
        <div className="bg-[#F45B5B] rounded-l-full" style={{ width: `${leftPercent}%` }} />
        <div className="bg-[#A4CF5E] rounded-r-full" style={{ width: `${rightPercent}%` }} />
      </div>
    </div>
  </div>
);

const TokenFlowItem = ({ token, isLast }: { token: TokenFlow; isLast: boolean }) => {
  const { currentCurrency } = useCurrencyContext();
  const { language } = useLanguageContext();

  // Calculate percentages
  const depositVolPercent = calculatePercentage(token.vol_deposit, token.volume);
  const withdrawVolPercent = calculatePercentage(token.vol_withdraw, token.volume);
  const depositTxPercent = calculatePercentage(token.no_deposit, token.no_txns);
  const withdrawTxPercent = calculatePercentage(token.no_withdraw, token.no_txns);
  const depositUserPercent = calculatePercentage(token.no_user_deposit, token.users);
  const withdrawUserPercent = calculatePercentage(token.no_user_withdraw, token.users);

  return (
    <div>
      <div className="p-4 hover:bg-gray-50 rounded-lg transition-colors">
        <div className="space-y-4">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => window.open(`/${language}/markets/${token.symbol}`, "_blank")}
          >
            <Avatar src={token.logo_uri} size={32} />
            <div className="flex items-center gap-2">
              <Typography.Text strong className="text-lg">
                {token.symbol}
              </Typography.Text>
              <Typography.Text className="text-gray-500 text-lg">{token.name}</Typography.Text>
            </div>
          </div>

          <Divider className="m-0" />

          <div className="space-y-2">
            <MetricSection
              title="Volume"
              value={formatThousands(token.volume, currentCurrency)}
              leftLabel="Deposit Vol"
              rightLabel="Withdrawal Vol"
              leftValue={formatThousands(token.vol_deposit, currentCurrency)}
              rightValue={formatThousands(token.vol_withdraw, currentCurrency)}
              leftPercent={depositVolPercent}
              rightPercent={withdrawVolPercent}
            />

            <Divider className="!my-2" />

            <MetricSection
              title="Txs"
              value={token.no_txns}
              leftLabel="Deposits"
              rightLabel="Withdrawals"
              leftValue={formatThousands(token.no_deposit)}
              rightValue={formatThousands(token.no_withdraw)}
              leftPercent={depositTxPercent}
              rightPercent={withdrawTxPercent}
            />

            <Divider className="!my-2" />

            <MetricSection
              title="Users"
              value={token.users}
              leftLabel="Depositors"
              rightLabel="Withdrawers"
              leftValue={formatThousands(token.no_user_deposit)}
              rightValue={formatThousands(token.no_user_withdraw)}
              leftPercent={depositUserPercent}
              rightPercent={withdrawUserPercent}
            />
          </div>
        </div>
      </div>
      {!isLast && <Divider className="!my-2" />}
    </div>
  );
};

export default function TokenFlows() {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<TokenFlow[]>([]);

  const { data, isLoading } = useMyQuery<TokenFlowResponse>(
    {
      url: "/v1/dex-rating/cex/flows",
      params: {
        q: searchTerm,
        page,
        limit: 10,
      },
      includeParamsInQueryKey: true,
    },
    {
      queryKey: ["token-flows", searchTerm, page],
      refetchInterval: 15 * 60 * 1000,
    },
  );

  useEffect(() => {
    if (data?.data.result) {
      if (page === 1) {
        setItems(data.data.result);
      } else {
        setItems(prev => [...prev, ...data.data.result]);
      }
    }
  }, [data?.data.result, page]);

  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
      setItems([]);
      setPage(1);
    }, 300),
    [],
  );

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50 && !isLoading && data?.data.result.length === 10) {
        setPage(prev => prev + 1);
      }
    };

    const currentContainer = containerRef.current;
    if (currentContainer) {
      currentContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentContainer) {
        currentContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isLoading, data?.data.result.length]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-[0_2px_8px_rgba(0,0,0,0.1)] p-4">
      <Typography.Title level={5} className="!mb-4">
        CEX Token Flows
      </Typography.Title>
      <Input
        placeholder="Search for CEX"
        prefix={<SearchOutlined className="text-gray-400" />}
        onChange={e => handleSearch(e.target.value)}
        className="mb-4"
      />
      <div ref={containerRef} className="space-y-4 max-h-[400px] overflow-auto custom-scrollbar">
        {isLoading && page === 1 ? (
          <>
            <TokenFlowSkeleton />
            <TokenFlowSkeleton />
            <TokenFlowSkeleton />
          </>
        ) : !items.length ? (
          <div className="py-8">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Typography.Text type="secondary">
                  {searchTerm ? "No results found" : "No token flows yet"}
                </Typography.Text>
              }
            />
          </div>
        ) : (
          items.map((token, index) => (
            <TokenFlowItem key={token.symbol} token={token} isLast={index === items.length - 1} />
          ))
        )}
        {isLoading && page > 1 && (
          <div className="py-4">
            <TokenFlowSkeleton />
          </div>
        )}
      </div>
    </div>
  );
}

